/**
 * 제보 상태·유형의 **사람 말 이름표**.
 *
 * 상태 코드를 그대로 보여주지 않습니다 — 제보한 사람이 알고 싶은 것은
 * `NEEDS_MORE_INFO` 가 아니라 "확인할 게 남아 있다" 입니다.
 *
 * Web 화면이 이 표를 자기 파일 안에 들고 있었습니다. Native 가 같은 화면을
 * 만들면 표가 둘이 되고, 한쪽만 고치면 같은 제보가 플랫폼마다 다른 말을
 * 하게 됩니다. 카테고리·검증 이름표와 같은 자리로 올립니다.
 *
 * 상태 **전이 규칙**은 여기 없습니다. 그것은 `@oshimap/domain` 의
 * `canTransition` 이 압니다 — 규칙을 두 곳에 두지 않습니다 (P6).
 */
export type ReportTone = "wait" | "live" | "ok" | "no";

export interface ReportStatusLabel {
  readonly label: string;
  readonly tone: ReportTone;
  /** 그래서 지금 어떻게 되고 있는가. 빈 문자열이 아니라 실제 문장입니다. */
  readonly body: string;
}

export const REPORT_STATUS: Readonly<Record<string, ReportStatusLabel>> = {
  DRAFT:              { label: "임시저장",   tone: "wait", body: "아직 제출되지 않았어요." },
  SUBMITTED:          { label: "제출됨",     tone: "wait", body: "검수를 기다리고 있어요." },
  UNDER_REVIEW:       { label: "검토 중",    tone: "live", body: "확인하고 있어요." },
  /*
   * "출처를 더 알려주시면 도움이 됩니다" 라고 했는데 **더 알려줄 방법이
   * 없습니다.** 보낸 제보를 고치는 API 가 없습니다 (`reports` 에 수정
   * 엔드포인트가 없음). 도메인의 `isEditable` 은 이 상태를 "손댈 수 있다" 고
   * 하지만 그 길이 아직 뚫려 있지 않습니다.
   *
   * 할 수 없는 일을 시키지 않습니다. 지금 실제로 되는 것을 말합니다.
   */
  NEEDS_MORE_INFO:    { label: "추가 확인",  tone: "live", body: "확인할 게 남아 있어요. 아직 보낸 제보를 고칠 수는 없어서, 자세한 내용을 새 제보로 보내주시면 빠릅니다." },
  ON_HOLD:            { label: "보류",       tone: "wait", body: "지금은 판단을 미뤄 두었어요." },
  APPROVED:           { label: "반영됨",     tone: "ok",   body: "알려주신 내용이 반영됐어요." },
  PARTIALLY_APPROVED: { label: "일부 반영",  tone: "ok",   body: "일부만 반영됐어요." },
  REJECTED:           { label: "반려",       tone: "no",   body: "이번에는 반영하지 못했어요." },
  WITHDRAWN:          { label: "철회함",     tone: "no",   body: "직접 취소한 제보예요." },
};

/** 모르는 상태는 코드 그대로 보여줍니다 — 접어 버리면 새 상태가 생긴 것을 아무도 모릅니다. */
export function reportStatusLabel(status?: string | null): ReportStatusLabel {
  if (!status) return { label: "알 수 없음", tone: "wait", body: "" };
  return REPORT_STATUS[status] ?? { label: status, tone: "wait", body: "" };
}

/**
 * backend 의 `REPORT_TYPES` **16종 전부**에 이름이 있어야 합니다.
 *
 * `PLACE_EDIT` 와 `EVENT_EDIT` 가 빠져 있었습니다 — 그 유형의 제보를 보낸
 * 사람은 자기 "내 제보" 목록에서 `PLACE_EDIT` 라는 **영어 코드**를 봤습니다.
 * 빠졌다는 것을 이제 테스트가 잡습니다 (`config.test.ts`).
 */
export const REPORT_TYPE_LABEL: Readonly<Record<string, string>> = {
  NEW_PLACE: "새 장소", PLACE_CHANGE: "정보 수정", PLACE_EDIT: "장소 직접 수정",
  PLACE_CLOSED: "폐점", CLOSURE: "종료",
  NEW_EVENT: "새 이벤트", EVENT_CHANGE: "이벤트 수정", EVENT_EDIT: "이벤트 직접 수정",
  EVENT_ENDED: "이벤트 종료",
  WRONG_LOCATION: "위치 오류", WRONG_INFORMATION: "정보 오류", LIVE_UPDATE: "현장 소식",
  DUPLICATE_PLACE: "중복", DUPLICATE_EVENT: "중복", COPYRIGHT: "저작권", OTHER: "기타",
};

export function reportTypeLabel(type?: string | null): string {
  if (!type) return "기타";
  return REPORT_TYPE_LABEL[type] ?? type;
}
